import { motion } from "motion/react";
import {
  Monitor,
  Bus,
  Trophy,
  Waves,
  UtensilsCrossed,
  Bed,
} from "lucide-react";

const academicsFacilities = [
  {
    icon: Monitor,
    title: "ICT Centre & Modern Library",
    description:
      "Hilltop has a modern computer lab installed with up-to-date computer machines. The computer lab takes up to 20 students per session. With the introduction of the new education system 2-6-3-3-3, Hilltop Preparatory School is gearing towards providing and using computers as a media of learning and content delivery for all our children. The library provides more learning materials for the students over and above the school course books.",
    image:
      "/ict-centre.png",
  },
  {
    icon: Bus,
    title: "Transport",
    description:
      "Hilltop Preparatory School has invested in a modern transport system. The school buses are used to pick and drop day schooling students to and from their homes, and also for children’s educational and extra-curriculum school trips. The drivers and conductors of each school bus are trained on how to handle children. They are also closely monitored to ensure the safety of all our children while they are on transit on any Hilltop Preparatory school bus. All out vehicles are well maintained and fitted with safety devices e.g. speed governors and safety belts for each passenger.",
    image:
      "/transport.png",
  },
];

const extraCurricularFacilities = [
  {
    icon: Trophy,
    title: "Sporting Activities",
    description:
      "Hilltop prides itself on not only being the best in academic life but also in extra-curricular activities. Clubs are an integral weekly routine. Club activities are carefully selected to ensure that learning is stimulating, fulfilling, and unique nurturing the children’s interests and skills. The nature of activities that each club presents usually guides registration and membership. Be it drama, agriculture, tennis, or music. The student’s choice and interest are always paramount.",
    image:
      "/extra-curricular-1.png",
  },
  {
    icon: Waves,
    title: "Swimming Pool",
    description:
      "The school management is working on installing a competition-size swimming pool for school extra-curriculum purposes. The swimming pool will be available for all students (both boarders and day schoolers) according to the provided extra-curriculum timetable. The architectural plan is complete and construction starting soon.",
    image:
      "/swimming-pool.png",
  },
];

const boardingFacilities = [
  {
    icon: UtensilsCrossed,
    title: "Catering",
    description:
      "Hilltop offers nutritious and well-balanced meals that meet the needs of all the students in the school. We maintain strict policies on hygiene while handling food and all the standards concerning hygiene are met.",
    image:
      "/catering.png",
  },
  {
    icon: Bed,
    title: "Accommodation",
    description:
      "At Hilltop Preparatory School, we provide an optional boarding program for children in middle school, classes 3 to 5, and compulsory boarding for upper school students in classes 6 to 8. Boarding facilities are well managed and hygiene is strictly handled. Boys and girls have separate dormitories with a resident matron 24 hours 7 days a week during the school term. Hot water is provided for bathing.",
    image:
      "/accommodation.png",
  },
];

interface FacilitySectionProps {
  id: string;
  title: string;
  subtitle: string;
  facilities: typeof academicsFacilities;
  bgColor: string;
  accentColor: string;
}

function FacilitySection({
  id,
  title,
  subtitle,
  facilities,
  bgColor,
  accentColor,
}: FacilitySectionProps) {
  return (
    <section id={id} className={`py-20 ${bgColor}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span
            className={`inline-block px-4 py-2 ${accentColor} rounded-full text-sm font-medium mb-4`}
          >
            {subtitle}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">
            {title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {facilities.map((facility, index) => (
            <motion.div
              key={facility.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="group"
            >
              <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 h-full">
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={facility.image}
                    alt={facility.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />

                  {/* Icon */}
                  <div className="absolute bottom-4 left-4 w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <facility.icon className="w-7 h-7 text-blue-600" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-red-600 transition-colors">
                    {facility.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {facility.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FacilitiesContent() {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6 border border-white/20"
          >
            World-Class Infrastructure
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
          >
            Our Facilities
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-white/80 max-w-2xl mx-auto"
          >
            Discover the exceptional facilities that make Hilltop Preparatory a
            premier institution for holistic education.
          </motion.p>
        </div>
      </section>

      {/* Academics */}
      <FacilitySection
        id="academics"
        title="Academics"
        subtitle="Learning Excellence"
        facilities={academicsFacilities}
        bgColor="bg-white"
        accentColor="bg-blue-100 text-blue-700"
      />

      {/* Extra Curricular */}
      <FacilitySection
        id="extra-curricular"
        title="Extra Curricular"
        subtitle="Beyond the Classroom"
        facilities={extraCurricularFacilities}
        bgColor="bg-slate-50"
        accentColor="bg-red-100 text-red-700"
      />

      {/* Boarding */}
      <FacilitySection
        id="boarding"
        title="Boarding"
        subtitle="Home Away From Home"
        facilities={boardingFacilities}
        bgColor="bg-white"
        accentColor="bg-blue-100 text-blue-700"
      />
    </div>
  );
}
